import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { readDocument, selectDocumentRead } from "../../store/slices/aiSlice";
import { FileText, Upload, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import AIPanel, { AI_BTN, AI_INPUT, AI_LABEL } from "./AIPanel";

const DOC_TYPES = [
  { value: "gstin", label: "GSTIN Certificate" },
  { value: "pan", label: "PAN Card" },
  { value: "business_license", label: "Business License" },
  { value: "fssai", label: "FSSAI Certificate" },
  { value: "bank_proof", label: "Bank Proof (Cheque/Passbook)" },
];

const confidenceColors = {
  high: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border border-amber-200",
  low: "bg-red-50 text-red-700 border border-red-200",
};

export default function AIDocumentReader({ onExtracted }) {
  const dispatch = useDispatch();
  const { data, loading } = useSelector(selectDocumentRead);
  const [docType, setDocType] = useState("gstin");
  const fileRef = useRef();

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!docType) return toast.error("Select document type first");
    dispatch(readDocument({ file, documentType: docType }));
  };

  const extracted = data?.extracted;
  const confidence = data?.confidence;

  return (
    <AIPanel
      icon={FileText}
      title="AI Document Reader"
      description="Upload a document and the details are read out for you."
    >
      <label className={`block mb-1.5 ${AI_LABEL}`} htmlFor="ai-doc-type">Document type</label>
      <select
        id="ai-doc-type"
        value={docType}
        onChange={(e) => setDocType(e.target.value)}
        className={`${AI_INPUT} mb-4`}
      >
        {DOC_TYPES.map((d) => (
          <option key={d.value} value={d.value}>{d.label}</option>
        ))}
      </select>

      <div
        onClick={() => fileRef.current?.click()}
        className="group border-2 border-dashed border-gray-200 rounded-2xl px-6 py-10 text-center cursor-pointer hover:border-purple-300 hover:bg-purple-50/40 transition-all mb-4"
      >
        {loading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={28} className="animate-spin text-purple-500" />
            <p className="text-sm text-gray-700 font-semibold">Reading document...</p>
          </div>
        ) : (
          <>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-50 group-hover:bg-white transition-colors">
              <Upload size={22} className="text-purple-600" />
            </div>
            <p className="text-sm font-semibold text-gray-900">Upload document image or PDF</p>
            <p className="text-xs text-gray-500 mt-1">JPG, PNG, or PDF</p>
          </>
        )}
      </div>
      <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf" onChange={handleUpload} />

      {extracted && (
        <div className="rounded-2xl border border-gray-200/70 bg-gray-50/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-gray-900">Extracted Data</span>
            {confidence && (
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${confidenceColors[confidence] || confidenceColors.medium}`}>
                {confidence} confidence
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(extracted).map(([key, value]) => {
              if (!value) return null;
              return (
                <div key={key} className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-gray-200/70">
                  <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase font-semibold tracking-wider text-gray-400">{key.replace(/_/g, " ")}</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{String(value)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <button onClick={() => onExtracted?.(data)} className={`${AI_BTN} mt-4 w-full`}>
            Apply to Profile
          </button>
        </div>
      )}
    </AIPanel>
  );
}
